import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { formatNumber } from '@utils/utils';
import { useDetectClickOutside } from '@hooks/useDetectClickOutside/useDetectClickOutside';
import SvgAngleLeft from '@icons/angle-left.svg?react';
import SvgAngleRight from '@icons/angle-right.svg?react';
import SvgAnglesLeft from '@icons/angles-left.svg?react';
import SvgAnglesRight from '@icons/angles-right.svg?react';
import SvgChevron from '@icons/chevron.svg?react';
import { buildPaginationItems, clamp, PaginationItem } from './paginationItems';

interface ComponentProps {
  /** Total number of records across all pages. */
  totalRecords: number;
  /** Current page, 1-based (as returned by the API `page` field). */
  page: number;
  /** Current page size. */
  pageSize: number;
  rowsPerPageOptions: number[];
  /**
   * Row count at which the API stops counting and reports the cap itself. At or above it the total
   * is a floor, not a count, so it renders as "10 000+" instead of asserting an exact number.
   */
  totalCapAt?: number;
  /**
   * Emits the same event shape as PrimeReact's paginator so the existing {@link useFilter} wiring is
   * reused unchanged: `page` is 0-based, `rows` is the page size.
   */
  onPage: (event: { page: number; rows: number }) => void;
  isMobile: boolean;
}

// bg is set per-variant (not here) so the active chip's bg-content-brandMedium never collides with a base bg.
const itemBase = 'inline-flex items-center justify-center h-[33px] min-w-[33px] px-1 rounded border border-solid text-[14px] leading-none select-none transition-all duration-200';

// Shared hover for white/orange clickable items — matches RingilButton's outlined/table buttons.
const hoverBrandDark = 'hover:border-complementary-brandDark hover:text-complementary-brandDark';

const PageSizeDropdown: React.FC<{
  value: number;
  options: number[];
  isMobile: boolean;
  onChange: (size: number) => void;
}> = ({ value, options, isMobile, onChange }) => {
  const { tr } = useTranslationLgs();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useDetectClickOutside([ref], () => setOpen(false));

  const label = (size: number) => (isMobile ? `${size}` : `${size} ${tr('RingilPaginator.items', 'items')}`);

  return (
    <div className="flex items-center gap-2">
      {!isMobile && <span className="text-content-secondary text-[14px] whitespace-nowrap">{tr('RingilPaginator.show', 'Show')}</span>}
      <div ref={ref} className="relative">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="group flex items-center gap-2 bg-base-secondary border border-solid border-content-brandMedium px-2 py-[10px] rounded cursor-pointer transition-all duration-200 hover:border-complementary-brandDark">
          <span className="text-content-secondary text-[14px] font-medium whitespace-nowrap transition-colors duration-200 group-hover:text-complementary-brandDark">{label(value)}</span>
          <SvgChevron className={clsx('w-3 shrink-0 text-content-brand transition-all duration-200 group-hover:text-complementary-brandDark', { 'rotate-180': open })} />
        </button>
        {open && (
          <div className="absolute right-0 bottom-full mb-1 z-20 bg-base-secondary rounded shadow-md border border-solid border-content-quaternary overflow-hidden min-w-full">
            {options.map(opt => (
              <div
                key={opt}
                onClick={() => {
                  onChange(opt);
                  setOpen(false);
                }}
                className={clsx('px-3 py-2 text-[14px] cursor-pointer hover:bg-base-base whitespace-nowrap', opt === value ? 'text-content-brand font-medium' : 'text-content-secondary')}>
                {label(opt)}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const RingilPaginator: React.FC<ComponentProps> = ({ totalRecords, page, pageSize, rowsPerPageOptions, totalCapAt, onPage, isMobile }) => {
  const { tr } = useTranslationLgs();

  const totalPages = Math.max(1, Math.ceil((totalRecords || 0) / pageSize));
  const current = clamp(page || 1, 1, totalPages);

  const goTo = (target: number) => {
    const next = clamp(target, 1, totalPages);
    if (next !== current) onPage({ page: next - 1, rows: pageSize });
  };
  const changeSize = (size: number) => onPage({ page: 0, rows: size });

  const capped = totalCapAt != null && (totalRecords || 0) >= totalCapAt;

  const resultsLabel = (
    <span className={clsx('text-content-secondary whitespace-nowrap', isMobile ? 'text-[12px]' : 'text-[14px]')}>
      <span className="font-semibold">
        {formatNumber(capped ? totalCapAt : totalRecords || 0, 0)}
        {capped && '+'}
      </span>{' '}
      {tr('RingilPaginator.results', 'results')}
    </span>
  );

  const arrowButton = (icon: React.ReactNode, targetPage: number, disabled: boolean, key: string) => (
    <button
      key={key}
      type="button"
      disabled={disabled}
      onClick={() => goTo(targetPage)}
      className={clsx(itemBase, 'bg-base-secondary border-content-brandMedium', disabled ? 'opacity-50 text-content-secondary cursor-not-allowed' : clsx('text-content-brand cursor-pointer', hoverBrandDark))}>
      <span className="inline-flex w-4 h-4 items-center justify-center">{icon}</span>
    </button>
  );

  // ----- Mobile: skip/prev + editable jump input + next/skip + results + size -----
  const [jumpValue, setJumpValue] = useState(String(current));
  useEffect(() => setJumpValue(String(current)), [current]);

  // Desktop: clicking an ellipsis turns it into an inline "jump to page" input (Figma editing variant).
  // Declared here (before the mobile early-return) to keep hook order stable across layouts.
  const [editingEllipsis, setEditingEllipsis] = useState<PaginationItem | null>(null);
  const [ellipsisValue, setEllipsisValue] = useState('');

  const commitJump = () => {
    const parsed = parseInt(jumpValue, 10);
    if (Number.isNaN(parsed)) {
      setJumpValue(String(current));
      return;
    }
    const next = clamp(parsed, 1, totalPages);
    setJumpValue(String(next));
    goTo(next);
  };

  if (isMobile) {
    return (
      <div className="flex items-center justify-between w-full gap-2 px-4 py-6 font-['DM_Sans']">
        <div className="flex items-center gap-1">
          {arrowButton(<SvgAnglesLeft className="w-4 h-4" />, 1, current <= 1, 'first')}
          {arrowButton(<SvgAngleLeft className="w-4 h-4" />, current - 1, current <= 1, 'prev')}
          <input
            value={jumpValue}
            inputMode="numeric"
            onChange={e => setJumpValue(e.target.value.replace(/[^0-9]/g, ''))}
            onKeyDown={e => e.key === 'Enter' && commitJump()}
            onBlur={commitJump}
            aria-label={tr('RingilPaginator.currentPage', 'Current page')}
            className="h-[33px] w-[52px] text-center bg-base-secondary border border-content-secondary rounded text-[14px] text-content-primary outline-none focus:border-content-brand"
          />
          {arrowButton(<SvgAngleRight className="w-4 h-4" />, current + 1, current >= totalPages, 'next')}
          {arrowButton(<SvgAnglesRight className="w-4 h-4" />, totalPages, current >= totalPages, 'last')}
          {resultsLabel}
        </div>
        <PageSizeDropdown value={pageSize} options={rowsPerPageOptions} isMobile onChange={changeSize} />
      </div>
    );
  }

  // ----- Desktop: prev + numbered pages (with ellipsis) + next + results ... size -----
  // Clicking an ellipsis turns it into an inline "jump to page" input (Figma editing variant).
  const items = buildPaginationItems(current, totalPages);

  const commitEllipsis = () => {
    const parsed = parseInt(ellipsisValue, 10);
    setEditingEllipsis(null);
    setEllipsisValue('');
    if (!Number.isNaN(parsed)) goTo(clamp(parsed, 1, totalPages));
  };

  return (
    <div className="flex items-center justify-between w-full p-6 font-['DM_Sans']">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          {arrowButton(<SvgAngleLeft className="w-4 h-4" />, current - 1, current <= 1, 'prev')}
          {items.map((item, idx) => {
            if (item === 'start-ellipsis' || item === 'end-ellipsis') {
              if (editingEllipsis === item) {
                return (
                  <input
                    key={`${item}-${idx}`}
                    autoFocus
                    value={ellipsisValue}
                    inputMode="numeric"
                    placeholder="…"
                    onChange={e => setEllipsisValue(e.target.value.replace(/[^0-9]/g, ''))}
                    onKeyDown={e => (e.key === 'Enter' ? commitEllipsis() : e.key === 'Escape' ? setEditingEllipsis(null) : undefined)}
                    onBlur={commitEllipsis}
                    aria-label={tr('RingilPaginator.goToPage', 'Go to page')}
                    className="h-[33px] w-[52px] text-center bg-base-secondary border-2 border-content-secondary rounded text-[14px] text-content-primary outline-none focus:border-content-brand"
                  />
                );
              }
              return (
                <button
                  key={`${item}-${idx}`}
                  type="button"
                  onClick={() => {
                    setEllipsisValue('');
                    setEditingEllipsis(item);
                  }}
                  aria-label={tr('RingilPaginator.goToPage', 'Go to page')}
                  className={clsx(itemBase, 'bg-base-secondary border-content-brandMedium text-content-brand cursor-pointer', hoverBrandDark)}>
                  …
                </button>
              );
            }
            const active = item === current;
            return (
              <button
                key={item}
                type="button"
                onClick={() => goTo(item)}
                className={clsx(
                  itemBase,
                  active
                    ? 'bg-content-brandMedium border-content-brand text-content-primary font-semibold'
                    : clsx('bg-base-secondary border-content-brandMedium text-content-brand font-normal cursor-pointer', hoverBrandDark),
                )}>
                {formatNumber(item, 0)}
              </button>
            );
          })}
          {arrowButton(<SvgAngleRight className="w-4 h-4" />, current + 1, current >= totalPages, 'next')}
        </div>
        {resultsLabel}
      </div>
      <PageSizeDropdown value={pageSize} options={rowsPerPageOptions} isMobile={false} onChange={changeSize} />
    </div>
  );
};

export default RingilPaginator;